import type { AutoFixLoopPresentation } from '@/lib/auto-fix-loop/presentation';

import type { InboxLatestCubicReview } from '../types';

const MAX_BADGE_COUNT = 9;

export type CubicReviewerPresentation =
  | { kind: 'hidden' }
  | { kind: 'running' }
  | { kind: 'issues'; unresolvedCount: number; displayCount: string }
  | { kind: 'clean' }
  | { kind: 'failed'; failureStatus: 'failed' | 'timeout' | 'cancelled' };

/**
 * Maps the projected cubic review summary onto the review states that cubic's
 * slot in the inbox row (`PrInboxCubicStatus`) and its tooltip draw from. The
 * taxonomy mirrors the PR sidebar's cubic states (running / issues / clean /
 * failed / idle); the slot draws only the ones that need a look and stays
 * blank for the rest.
 */
export function getCubicReviewerPresentation(
  latestCubicReview: InboxLatestCubicReview | undefined,
): CubicReviewerPresentation {
  if (!latestCubicReview || latestCubicReview.status === 'idle') {
    return { kind: 'hidden' };
  }

  switch (latestCubicReview.status) {
    // 'pending' is never projected by the inbox API (PR_V2_CUBIC_REVIEW_STATUSES
    // excludes it) but lives in ReviewRunStatus; a queued run reads as reviewing.
    case 'pending':
    case 'running':
      return { kind: 'running' };
    case 'completed':
    case 'skipped': {
      // The count is PR-scoped (unresolved violations across runs), so it stays
      // meaningful for skipped runs; the DB column is NOT NULL DEFAULT 0, making
      // undefined type-only here.
      const unresolvedCount = latestCubicReview.unresolvedCommentCount ?? 0;
      if (unresolvedCount > 0) {
        return {
          kind: 'issues',
          unresolvedCount,
          displayCount:
            unresolvedCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(unresolvedCount),
        };
      }
      return { kind: 'clean' };
    }
    case 'failed':
    case 'timeout':
    case 'cancelled':
      return { kind: 'failed', failureStatus: latestCubicReview.status };
    default: {
      latestCubicReview.status satisfies never;
      return { kind: 'hidden' };
    }
  }
}

/**
 * Human-readable status for cubic's slot: its tooltip and aria-label.
 * Always uses the full unresolved count, even when the slot caps at "9+".
 */
export function getCubicReviewerStatusLabel(
  presentation: CubicReviewerPresentation,
): string | null {
  switch (presentation.kind) {
    case 'hidden':
      return null;
    case 'running':
      return 'Reviewing code';
    case 'issues':
      return `${presentation.unresolvedCount} unresolved issue${presentation.unresolvedCount === 1 ? '' : 's'}`;
    case 'clean':
      return 'No unresolved issues';
    case 'failed':
      switch (presentation.failureStatus) {
        case 'failed':
          return 'Review failed';
        case 'timeout':
          return 'Review timed out';
        case 'cancelled':
          return 'Review canceled';
      }
  }
}

/**
 * What cubic is doing on a pull request, or null while it is not on it: the auto-fix loop's
 * sentence while the loop fixes (review issues or failing CI checks) or waits for cubic's next
 * review or CI result, else the review's status while cubic reviews.
 */
export function describeCubicWork(
  latestCubicReview: InboxLatestCubicReview | undefined,
  autoFix: AutoFixLoopPresentation | null,
): string | null {
  if (autoFix?.phase === 'working') {
    return autoFix.description;
  }
  const review = getCubicReviewerPresentation(latestCubicReview);
  return review.kind === 'running' ? getCubicReviewerStatusLabel(review) : null;
}
